<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import {
    getCountryList,
    markerStyles,
    type Country,
    type MapEngine,
  } from '@atlasguess/engine';
  import { createAudio, type AudioPlayer } from '@atlasguess/audio';
  import {
    applyEngineConfig,
    filterCountries,
    type CountryQuizConfig,
  } from '@atlasguess/games';
  import Prompt from '../framework/Prompt.svelte';
  import QuizHud from '../framework/QuizHud.svelte';
  import QuizStartScreen from '../framework/QuizStartScreen.svelte';
  import QuizEndScreen from '../framework/QuizEndScreen.svelte';
  import { soundEnabled } from '../../lib/sound';
  import { recordRun } from '../../lib/runs';
  import { mobileBumpedMarker } from '../../lib/mobile';

  interface Props {
    config: CountryQuizConfig;
    engine: MapEngine;
    /** ModeFrame's reveal-veil callback. Called once polygons are in. */
    markReady: () => void;
  }

  let { config, engine, markReady }: Props = $props();

  // 'ready' = data + camera primed; waiting for the player to click
  // Start before the timer begins. Restart and Retry skip 'ready' and
  // jump straight back to 'awaiting-guess' (player already committed).
  type Phase =
    | 'loading'
    | 'ready'
    | 'awaiting-guess'
    | 'pending-reveal'
    | 'finished';

  let phase = $state<Phase>('loading');

  /** Ordered, run-frozen pool — shuffled fresh on every restart. */
  let pool = $state<Country[]>([]);
  /**
   * ISOs in the pool — used as the click-acceptance gate AND to decide
   * which countries get small-country dots. Out-of-pool countries are
   * greyed via the engine's `inactive` fill state and silently ignore
   * clicks. Recomputed (cheaply) on every pool change.
   */
  const poolIsos = $derived(new Set(pool.map((c) => c.iso_a2)));
  /** 0-indexed pointer into the pool. Out-of-bounds → finished. */
  let roundIndex = $state(0);
  /** Wrong clicks for the CURRENT target only — cleared on advance. */
  let wrongAttempts = $state(0);
  /** Wrong-click ISOs this round — tracked so we can revert their fills. */
  let wrongMarksThisRound = $state<Set<string>>(new Set());
  /**
   * ISOs of countries the player has already answered (first-try or via
   * reveal). Clicks on these are silent no-ops — they shouldn't count
   * as wrong attempts (the country's already settled), and overwriting
   * their `correct` fill with `incorrect` would visually break the
   * "what I've answered so far" board state. Populated in advance()
   * once a target resolves; cleared on primeRun() so a Restart starts
   * with a fresh pool of clickable countries.
   */
  let answeredIsos = $state<Set<string>>(new Set());
  /** Number of targets resolved on the very first attempt — accuracy numerator. */
  let firstTryCorrect = $state(0);

  /** Run wall-clock — null before first round + while finished. */
  let startedAtMs = $state<number | null>(null);
  let finishedAtMs = $state<number | null>(null);
  /** Per-round wall-clock and resolved ms — populated as the run progresses.
   *  `firstTry` carries whether the round was solved on the first click,
   *  which the server-side aggregator uses to compute "best run" by
   *  (correct DESC, ms ASC). */
  let roundStartedAtMs: number | null = null;
  let roundResults: { iso: string; ms: number; firstTry: boolean }[] = [];
  /** Stable run id — set on handleStart, included in the recorded run_key. */
  let runStartedAtMsForKey: number | null = null;

  let audio: AudioPlayer | undefined;
  let restoreEngineConfig: (() => void) | null = null;
  let allCountries: Country[] = [];

  /** Engine markers layer for small-country dots — cleared on unmount. */
  const SMALL_COUNTRY_LAYER = 'country_pins';

  /**
   * Quiz reveals the answer in cyan (matches the `selected` polygon
   * default) instead of the engine's default gold for `revealed`. Gold
   * is reserved for city-distance "answer reveal" pin colors elsewhere;
   * cyan reads as "study this — you missed it" for tap quizzes.
   */
  const REVEAL_FILL_COLOR = '#5fb6cd';
  const DEFAULT_REVEAL_FILL_COLOR = '#d4a24c';

  const currentTarget = $derived(pool[roundIndex] ?? null);
  const elapsedMsForEnd = $derived(
    finishedAtMs !== null && startedAtMs !== null ? finishedAtMs - startedAtMs : null,
  );

  // Practice-mode link only makes sense for the World Practice mode for
  // now; a more elaborate "related modes" map can come later.
  const PRACTICE_HREF = '/play/countries-world/practice';

  /**
   * Absolute URL pointing at THIS quiz mode — fed into the share content
   * so the link a recipient clicks lands on the same mode. Computed
   * lazily so SSR / pre-render doesn't trip over `window`.
   */
  const modeUrl = $derived.by((): string => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/play/${config.scope.entity}-${config.scope.region}/${config.format}`;
  });

  onMount(() => {
    audio = createAudio({ enabled: $soundEnabled });
    engine.setOnCountryClick(handleClick);
    void initialize();
  });

  // Live-track the user's sound setting — flipping it in /account
  // silences future dings without needing to remount the mode.
  $effect(() => {
    audio?.setEnabled($soundEnabled);
  });

  async function initialize(): Promise<void> {
    await engine.loadBorders();
    restoreEngineConfig = applyEngineConfig(engine, config.layers);
    // Quiz overrides the polygon `revealed` color from gold to cyan so
    // "answer was here" reads distinctly from the city-mode gold reveal
    // pins. Restored on unmount via the cleanup below.
    engine.layers.outline.setColorTokens({ revealed: REVEAL_FILL_COLOR });
    // Animate the camera into the region — engine starts at default
    // world view; the easing tween reads as a deliberate "settling into
    // your region" pan. Combined with the loading veil fading at the
    // same time (markReady fires moments later), the user sees the
    // tail end of the pan as gameplay UI appears.
    engine.setCamera({
      lat: config.cameraView.lat,
      lon: config.cameraView.lon,
      zoom: config.cameraView.zoom,
      animate: true,
    });
    // NOTE: skip engine.autoPlaceNeedsDotMarkers — it would add a dot
    // for every needs_dot country in the world. Region quizzes only
    // want dots for in-pool countries; primeRun's repaintAllDots
    // creates them only for the filtered pool below.

    allCountries = await getCountryList();
    const filtered = filterCountries(allCountries, config.countryFilter);
    if (filtered.length === 0) {
      console.warn('[atlas] quiz pool is empty; mode cannot start');
      return;
    }

    // Pre-shuffle the pool + reset counters but DO NOT start the
    // timer — wait for the Start click. The player can browse the
    // region during the camera pan and start when ready.
    primeRun(filtered);
    phase = 'ready';
    markReady();
  }

  /**
   * Reset the per-run state without starting the timer. Used at initial
   * load (so the Start screen has accurate `total` and the engine state
   * is clean) and on Restart / Retry as the first half of the reset.
   */
  function primeRun(candidates: Country[]): void {
    pool = shuffle(candidates);
    roundIndex = 0;
    wrongAttempts = 0;
    wrongMarksThisRound = new Set();
    answeredIsos = new Set();
    firstTryCorrect = 0;
    finishedAtMs = null;
    startedAtMs = null;
    engine.layers.outline.clearFillStates();
    // Grey out everything not in this run's pool — the player should
    // see at a glance what's part of the game. `poolIsos` is freshly
    // derived from the new `pool` above (Svelte $derived runs sync).
    for (const c of allCountries) {
      if (!poolIsos.has(c.iso_a2)) {
        engine.layers.outline.setFillState(c.iso_a2, 'inactive');
      }
    }
    // Clear any stale dots from a previous pool, then re-place dots
    // ONLY for in-pool countries (so the Vatican dot doesn't show up
    // when running the Africa quiz, etc.).
    engine.markers.clear(SMALL_COUNTRY_LAYER);
    repaintAllDots('neutral');
  }

  /**
   * Begin gameplay — timer runs from this moment. Called by the Start
   * screen's button + by Restart / Retry (which prime + start in a
   * single beat so the player is back in gameplay immediately).
   */
  function handleStart(): void {
    startedAtMs = performance.now();
    roundStartedAtMs = startedAtMs;
    roundResults = [];
    runStartedAtMsForKey = Date.now();
    phase = 'awaiting-guess';
  }

  /**
   * Fisher-Yates in-place shuffle producing a new array. Math.random is
   * fine — the quiz isn't reproducible-share like Daily, every run is
   * intentionally fresh.
   */
  function shuffle<T>(items: readonly T[]): T[] {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  }

  function handleClick(country: Country | null): void {
    if (!country || !audio) return;
    // Out-of-pool countries are visually greyed AND silently
    // unclickable — matches the user's expectation that "not in
    // this game" doesn't react to taps. No audio, no fill change.
    if (!poolIsos.has(country.iso_a2)) return;

    // Already-answered countries (green from a prior round) are
    // no-ops with a soft neutral tone — gives the player auditory
    // feedback that the click registered without penalizing them
    // (incrementing wrongAttempts) or overwriting the persistent
    // `correct` fill with the wrong-click `incorrect` color. Single
    // mid-tone (G4) at half-default gain and a third-default tail
    // so it's clearly distinct from the bright correct chord and
    // the low incorrect tone.
    if (answeredIsos.has(country.iso_a2)) {
      audio.ding({ frequencies: 392, gain: 0.06, durationMs: 250 });
      return;
    }

    if (phase === 'awaiting-guess') return handleAwaitingGuess(country);
    if (phase === 'pending-reveal') return handlePendingReveal(country);
  }

  function handleAwaitingGuess(country: Country): void {
    if (!currentTarget || !audio) return;

    if (country.iso_a2 === currentTarget.iso_a2) {
      // First-try-correct counts toward the run accuracy stat. Late-correct
      // (after one or more wrongs) still resolves the round but doesn't.
      if (wrongAttempts === 0) firstTryCorrect++;
      audio.ding({ frequencies: config.audio.correctFrequencies });
      advance();
      return;
    }

    // Wrong click — mark it, increment attempt counter, possibly reveal.
    wrongMarksThisRound.add(country.iso_a2);
    wrongMarksThisRound = wrongMarksThisRound; // reactivity hint
    engine.layers.outline.setFillState(country.iso_a2, 'incorrect');
    paintCountryDot(country, 'incorrect');
    audio.ding({ frequencies: config.audio.incorrectFrequencies });
    wrongAttempts++;

    if (wrongAttempts >= config.attemptsPerCountry) {
      // Reveal the target in blue — player must click it to advance.
      engine.layers.outline.setFillState(currentTarget.iso_a2, 'revealed');
      paintCountryDot(currentTarget, 'revealed');
      // Gently rotate to the target so the player can find the
      // revealed country instead of hunting for the cyan polygon.
      // Keep current zoom — only rotation changes; feels like the
      // globe is "showing them the answer".
      engine.setCamera({
        lat: currentTarget.centroid[1],
        lon: currentTarget.centroid[0],
        animate: true,
      });
      phase = 'pending-reveal';
    }
  }

  function handlePendingReveal(country: Country): void {
    if (!currentTarget || !audio) return;
    if (country.iso_a2 !== currentTarget.iso_a2) {
      // After the reveal, only clicking the target advances. Other clicks
      // are silently ignored — no audio, no state change.
      return;
    }
    audio.ding({ frequencies: config.audio.correctFrequencies });
    advance();
  }

  /**
   * Resolve the current target as correct (green, persistent), clear this
   * round's wrong marks, and move on. Used for both first-try-correct
   * and click-on-revealed flows — same end state in both cases.
   */
  function advance(): void {
    if (!currentTarget) return;
    // Capture first-try-correct status BEFORE the wrongAttempts reset
    // below — the round result needs the honest flag, and the same
    // advance() runs for both first-try and reveal-then-clicked paths.
    // wrongAttempts === 0 is true only when the player clicked the
    // right country on their first attempt this round.
    const wasFirstTry = wrongAttempts === 0;

    engine.layers.outline.setFillState(currentTarget.iso_a2, 'correct');
    paintCountryDot(currentTarget, 'correct');
    // Mark this country as answered so future clicks on it (a
    // misclick while hunting for the next target, or just exploring)
    // are short-circuited in handleClick — same set covers both
    // first-try and reveal-then-clicked paths since both go through
    // advance(). Cloned-and-reassigned for Svelte 5 reactivity.
    answeredIsos = new Set(answeredIsos).add(currentTarget.iso_a2);
    // Wrong marks are noise once the round resolves — clear them so the
    // accumulated visual stays focused on what's been learned.
    for (const iso of wrongMarksThisRound) {
      const c = countryByIso(iso);
      engine.layers.outline.setFillState(iso, null);
      if (c) paintCountryDot(c, 'neutral');
    }
    wrongMarksThisRound = new Set();
    wrongAttempts = 0;

    // Record this round's resolution time + first-try flag before
    // moving on. Captured for both first-try-correct and reveal-then-
    // correct paths so total ms sums to the wall-clock the player
    // experiences and best-run computation has accurate accuracy data.
    if (roundStartedAtMs !== null) {
      const now = performance.now();
      roundResults.push({
        iso: currentTarget.iso_a2,
        ms: now - roundStartedAtMs,
        firstTry: wasFirstTry,
      });
      roundStartedAtMs = now;
    }

    if (roundIndex + 1 >= pool.length) {
      finishedAtMs = performance.now();
      phase = 'finished';
      // Quiz writes one row per round, all under a per-run run_key so the
      // server aggregator groups them as a single quiz attempt.
      if (runStartedAtMsForKey !== null) {
        void recordRun({
          mode_id: config.id,
          format: 'quiz',
          run_key: `${config.id}-${runStartedAtMsForKey}`,
          rounds: roundResults.map((r) => ({
            target_id: r.iso,
            score: r.ms,
            score_unit: 'ms',
            first_try: r.firstTry,
          })),
        });
      }
      return;
    }
    roundIndex++;
    phase = 'awaiting-guess';
  }

  function handleRestart(): void {
    if (allCountries.length === 0) return;
    const filtered = filterCountries(allCountries, config.countryFilter);
    // Re-frame the camera animated this time — feels gentler than a snap
    // when the user has already been looking at the globe.
    engine.setCamera({
      lat: config.cameraView.lat,
      lon: config.cameraView.lon,
      zoom: config.cameraView.zoom,
      animate: true,
    });
    // Restart / Retry skips the Start screen — player has already
    // committed by clicking the button; making them tap Start again
    // would be friction.
    primeRun(filtered);
    handleStart();
  }

  // -- helpers --

  function countryByIso(iso: string): Country | undefined {
    return allCountries.find((c) => c.iso_a2 === iso);
  }

  /**
   * Re-tint a small-country dot to match the polygon highlight states.
   * Mirrors the practice-mode helper — only touches countries flagged
   * `needs_dot`. Same `add(id, ...)`-replaces-existing contract.
   */
  function paintCountryDot(country: Country, variant: 'neutral' | 'correct' | 'incorrect' | 'revealed'): void {
    if (!country.needs_dot) return;
    const style =
      variant === 'correct'
        ? markerStyles.countryPinCorrect
        : variant === 'incorrect'
          ? markerStyles.countryPinIncorrect
          : variant === 'revealed'
            ? markerStyles.countryPinRevealed
            : markerStyles.countryPin;
    engine.markers.add({
      id: `country-pin-${country.iso_a2}`,
      position: { lat: country.centroid[1], lon: country.centroid[0] },
      style: mobileBumpedMarker(style),
      layer: SMALL_COUNTRY_LAYER,
      data: country,
    });
  }

  /** Bulk-paint every in-pool small-country dot — used by primeRun to
   *  create + reset. Iterates `pool`, NOT `allCountries`, so out-of-pool
   *  needs_dot countries (Vatican when running Africa, etc.) don't get
   *  a stray dot floating over the inactive grey. */
  function repaintAllDots(variant: 'neutral'): void {
    for (const c of pool) {
      if (c.needs_dot) paintCountryDot(c, variant);
    }
  }

  onDestroy(() => {
    engine.setOnCountryClick(undefined);
    engine.layers.outline.clearFillStates();
    // Restore the engine's default reveal color so the next mode that
    // touches `revealed` (city distance arc reveal etc.) doesn't inherit
    // the quiz's cyan override.
    engine.layers.outline.setColorTokens({ revealed: DEFAULT_REVEAL_FILL_COLOR });
    engine.markers.clear(SMALL_COUNTRY_LAYER);
    audio?.cancel();
    audio?.dispose();
    restoreEngineConfig?.();
  });
</script>

{#if phase === 'ready'}
  <QuizStartScreen
    quizName={config.displayName}
    total={pool.length}
    onStart={handleStart}
  />
{/if}

{#if phase === 'awaiting-guess' && currentTarget}
  <Prompt
    primary={currentTarget.name}
    flagUrl={`/${currentTarget.flag_file}`}
    active={true}
  />
{/if}

{#if phase === 'pending-reveal' && currentTarget}
  <Prompt
    lead="Click the highlighted country to continue:"
    primary={currentTarget.name}
    flagUrl={`/${currentTarget.flag_file}`}
    active={true}
  />
{/if}

{#if phase === 'awaiting-guess' || phase === 'pending-reveal'}
  <QuizHud
    {startedAtMs}
    frozenElapsedMs={null}
    resolved={roundIndex}
    total={pool.length}
    onRestart={handleRestart}
  />
{/if}

{#if phase === 'finished'}
  <QuizEndScreen
    elapsedMs={elapsedMsForEnd ?? 0}
    {firstTryCorrect}
    total={pool.length}
    quizName={config.displayName}
    {modeUrl}
    onRetry={handleRestart}
    practiceHref={PRACTICE_HREF}
  />
{/if}
